import React, {useState, useMemo} from 'react'
import {Box, Link, HStack, Image, VStack, Text, useSlotRecipe, useRecipe} from '@chakra-ui/react3'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {PROMOTION_BADGE_MESSAGES} from '../../../constants'
import useProductSpecialOffers from '../../../hooks/use-product-special-offers'
import {ChevronDownIcon, ChevronUpIcon} from '../icons'
import Slider from '../cms/slider'
import StyledContent from '../styled-content'
import {
    promotionBannerRecipe,
    promotionItemRecipe,
    sliderNavigationButtonVariant,
    sliderPaginationVariant
} from './index.recipe'
import {Chakra3Theme} from '../../theme'

const PromotionItem = ({promotion, isActive, theme}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [isIconURLValid, setIsIconURLValid] = useState(true)
    const {formatMessage} = useIntl()

    const [prevIsActive, setPrevIsActive] = useState(isActive)

    if (isActive !== prevIsActive) {
        setPrevIsActive(isActive)

        if (!isActive) {
            setIsOpen(false)
        }
    }

    const colorWorld = promotion?.c_colorWorld?.toLowerCase()
    const itemRecipe = useSlotRecipe({recipe: promotionItemRecipe})
    const styles = useMemo(() => itemRecipe({colorWorld}), [itemRecipe, colorWorld])

    return (
        <Chakra3Theme theme={theme}>
            <VStack align="start" width="full" gap="spacing-8" pb="spacing-16">
                <HStack gap="spacing-8" align="center">
                    {promotion?.c_promotionIcon && isIconURLValid && (
                        <Box css={styles.iconBox}>
                            <Image
                                alt=""
                                src={promotion.c_promotionIcon}
                                objectFit="contain"
                                flexShrink={0}
                                onError={() => setIsIconURLValid(false)}
                            />
                        </Box>
                    )}
                    <Text css={styles.heading}>
                        {PROMOTION_BADGE_MESSAGES[promotion?.c_badge]
                            ? formatMessage(PROMOTION_BADGE_MESSAGES[promotion.c_badge])
                            : promotion.name}
                    </Text>
                </HStack>

                <Box css={styles.content}>
                    <StyledContent content={promotion?.calloutMsg} />
                </Box>

                {isOpen && promotion.details && (
                    <Box
                        ref={(el) => el?.focus()}
                        tabIndex="-1"
                        css={styles.content}
                        className="leap-container"
                    >
                        <StyledContent content={promotion?.details} />
                    </Box>
                )}

                {promotion.details && (
                    <Link
                        as="button"
                        variant="secondary"
                        underline
                        size="small"
                        onClick={() => setIsOpen(!isOpen)}
                        aria-expanded={isOpen}
                    >
                        {isOpen ? (
                            <FormattedMessage
                                id="leap.promotion.show_less"
                                defaultMessage="Show less"
                            />
                        ) : (
                            <FormattedMessage
                                id="leap.promotion.show_more"
                                defaultMessage="Show more"
                            />
                        )}
                        {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </Link>
                )}
            </VStack>
        </Chakra3Theme>
    )
}

PromotionItem.propTypes = {
    promotion: PropTypes.object,
    isActive: PropTypes.bool,
    theme: PropTypes.string
}

const PromotionBanner = ({product}) => {
    const {formatMessage} = useIntl()
    const [activeIndex, setActiveIndex] = useState(0)

    const {promotions, loading} = useProductSpecialOffers(product, null, 'xpdp')

    const activePromotion = promotions?.[activeIndex]
    const activeColorWorld = activePromotion?.c_colorWorld?.toLowerCase()

    const bannerRecipe = useRecipe({recipe: promotionBannerRecipe})
    const bannerStyles = useMemo(() => bannerRecipe(), [bannerRecipe])

    const theme = activeColorWorld === 'black' ? 'dark' : 'light'

    if (loading || !promotions?.length) return null

    const isSingleItem = promotions.length === 1

    return (
        <Chakra3Theme theme={theme}>
            <Box
                as="section"
                aria-label={formatMessage({
                    id: 'leap.promotion.assistive_msg.section_label',
                    defaultMessage: 'promotion'
                })}
                css={bannerStyles}
            >
                {isSingleItem ? (
                    <PromotionItem promotion={promotions[0]} isActive theme={theme} />
                ) : (
                    <Slider
                        variant="promotionBanner"
                        sliderLabel="Product Promotions"
                        onIndexChange={(index) => setActiveIndex(index)}
                        navigationButtonVariant={
                            sliderNavigationButtonVariant[activeColorWorld] || 'secondaryGhost'
                        }
                        paginationVariant={sliderPaginationVariant[activeColorWorld]}
                    >
                        {promotions.map((promo, idx) => (
                            <PromotionItem
                                key={promo.id}
                                promotion={promo}
                                isActive={activeIndex === idx}
                                theme={theme}
                            />
                        ))}
                    </Slider>
                )}
            </Box>
        </Chakra3Theme>
    )
}

PromotionBanner.propTypes = {
    product: PropTypes.object
}

export default PromotionBanner
